import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Input from "@/components/common/inputs/Input";
import WebIcon from "@/components/common/Web_Icons";
import CountryFlag from "@/components/common/Country_Flag";
import ProfilePicker from "@/components/specific/Profile_Picker";
import {
  selectOrganization,
  updateOrganization,
  fetchOrganizationById,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import { useLoader } from "@/hooks/LoaderProvider";

const CorporateInfo = forwardRef(
  ({ userProfile = null, onProfileImageChange }, ref) => {
    const dispatch = useDispatch();
    const { showLoader, hideLoader } = useLoader();
    const { organizationData, isLoading } = useSelector(selectOrganization);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [formData, setFormData] = useState({
      organizationType: "",
      role: "",
      name: "",
      address: "",
      email: "",
      phone: "",
    });

    const organizationTypeOptions = [
      { value: "LLC", label: "Limited Liability Company" },
      { value: "CORPORATION", label: "Corporation" },
      { value: "PARTNERSHIP", label: "Partnership" },
    ];

    const roleOptions = [
      { value: "Super Admin", label: "Super Admin" },
      { value: "Campaign Manager", label: "Campaign Manager" },
      { value: "Event Admin", label: "Event Admin" },
      { value: "Finance Admin", label: "Finance Admin" },
      { value: "Communication Manager", label: "Communication Manager" },
      { value: "Canvasser", label: "Canvasser" },
      { value: "Volunteer", label: "Volunteer" },
    ];

    useEffect(() => {
      // Skip mount fetch when SettingsMenu (or another screen) already cached org data.
      if (Array.isArray(organizationData) && organizationData.length > 0) {
        return;
      }

      const fetchOrganization = async () => {
        try {
          await dispatch(fetchOrganizationById()).unwrap();
        } catch (error) {
          if (error?.name === "ConditionError") return;
          console.error("Error fetching organization:", error);
        }
      };
      fetchOrganization();
    }, [dispatch, organizationData]);

    useEffect(() => {
      if (organizationData) {
        setFormData({
          organizationType: organizationData[0]?.type || "",
          role: organizationData[0]?.role || "",
          name: organizationData[0]?.name || "",
          address: organizationData[0]?.address || "",
          email: organizationData[0]?.email || "",
          phone: organizationData[0]?.phone || "",
          _id: organizationData[0]?._id || "",
        });
      }
    }, [organizationData]);

    const handleInputChange = (field, value) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    // Expose handleSubmit to parent
    useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        showLoader();
        try {
          const updateData = [
            {
              name: formData.name,
              type: formData.organizationType,
              role: formData.role,
              address: formData.address,
              email: formData.email,
              phone: formData.phone,
              _id: formData._id,
            },
          ];

          await dispatch(updateOrganization(updateData)).unwrap();

          setShowSuccess(true);
        } catch (err) {
          setErrorMessage(
            err.message || "An error occurred while updating the organization"
          );
          setShowError(true);
        } finally {
          hideLoader();
        }
      },
    }));

    const handleSuccessClose = async () => {
      setShowSuccess(false);
      setShowError(false);

      try {
        await dispatch(fetchOrganizationById({ force: true })).unwrap();
      } catch (error) {
        if (error?.name === "ConditionError") return;
        console.error("Error refreshing organization:", error);
      }
    };

    return (
      <div className="mt-4">
        {/* Success Modal */}
        <Success
          isVisible={showSuccess}
          onClose={handleSuccessClose}
          header="Organization Updated Successfully!"
          paragraph="Your organization information has been updated successfully."
          buttonText="Continue"
          showCancelButton={true}
          cancelButtonText="Close"
        />

        {/* Error Modal */}
        <Error
          isVisible={showError}
          onClose={() => setShowError(false)}
          header="Update Failed"
          paragraph={errorMessage}
          buttonText="Try Again"
          recoveryPath=""
        />

        <h2 className="text-bold mb-2">
          Basic Organization&apos;s Information
        </h2>
        <p className="text-regular text-lighterGrey mb-4">
          This will be used to define your organization&apos;s identity on the
          platform for ease of recognition as well as to easily manage your
          activities on the platform.
        </p>

        <ProfilePicker
          currentImage={userProfile?.profileImage}
          onImageChange={onProfileImageChange}
          className="mb-6"
        />

        <div className="mb-4">
          <Input
            label="ORGANIZATION FULL NAME"
            placeholder="Enter organization name"
            value={isLoading ? "Loading..." : formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <Input
            label="ORGANIZATION TYPE"
            placeholder="Select organization type"
            value={isLoading ? "Loading..." : formData.organizationType}
            onChange={(e) =>
              handleInputChange("organizationType", e.target.value)
            }
            type="select"
            options={organizationTypeOptions}
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <Input
            label="ROLE IN ORGANIZATION"
            placeholder="Select role"
            value={isLoading ? "Loading..." : formData.role}
            onChange={(e) => handleInputChange("role", e.target.value)}
            type="select"
            options={roleOptions}
            disabled={isLoading}
          />
        </div>

        <h2 className="mt-8 mb-2 text-bold">Organization&apos;s Location</h2>

        <div className="mb-4">
          <Input
            label="ORGANIZATION ADDRESS"
            placeholder="Enter address"
            value={isLoading ? "Loading..." : formData.address}
            onChange={(e) => handleInputChange("address", e.target.value)}
            icon={<WebIcon icon="Location" size="small" />}
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <Input
            label="EMAIL ADDRESS"
            placeholder="Enter email"
            value={isLoading ? "Loading..." : formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            type="email"
            icon={<WebIcon icon="Message" size="small" />}
            disabled={isLoading}
          />
        </div>

        <div className="mb-4">
          <Input
            label="ORGANIZATION PHONE NUMBER"
            placeholder="Enter phone number"
            value={isLoading ? "Loading..." : formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            type="tel"
            icon={<CountryFlag />}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  }
);

CorporateInfo.propTypes = {
  userProfile: PropTypes.object,
  onProfileImageChange: PropTypes.func,
};

// Add display name
CorporateInfo.displayName = "CorporateInfo";

export default CorporateInfo;
